import { DefaultButton, Spinner, Dialog, PrimaryButton, Separator, ChoiceGroup, DialogFooter, MessageBarType, SpinnerSize, MessageBar, PivotItem, Pivot, ActionButton } from '@fluentui/react';
import React, { useState, useEffect } from 'react'
import { AppStoreIdentity, AppType } from '../../../GlobalConst/globalEnums';
import Api from '../../../services/Api'
import CustomTextField from '../SiteEditElements/CustomTextField';
import CustomDropDown from '../SiteEditElements/CustomDropDown';
import DimensionPicker from '../SiteEditElements/DimensionPicker';
import LoadingIcon from '../../formFields/LoadingIcon';
import { useSelector } from 'react-redux';
import UploadFile from '../../CustomComponents/UploadFile';
import AppStoreSubmissionHistory from './AppStoreSubmissionHistory';
import InfoTooltip from '../../formFields/InfoTooltip';
import CustomRadioButton from '../SiteEditElements/CustomRadioButton';

const AppStoreSubmissionForm = ({solution, showDialog, setShowDialog, onSaveCallback, submissionItems}) => {

    const defaultFormData = solution && submissionItems && submissionItems.length 
        ? {
            ...submissionItems[0],
            solutionId: solution.id,
            ardoqIds: submissionItems[0].ardoq.map(x => x.id).join(','),
            catalogId: submissionItems[0].catalog.id
        }
        : {
            solutionId: solution.id,
            publisher: AppStoreIdentity.original,
            author: AppStoreIdentity.original,
            isManagedItem: null,
            primaryComponentId: '-1'
        }
    
    const solutionComponents = [{key: '-1', text: 'Unspecified'}].concat(solution.apps.map(x => ({key: x.id.toString(), text: x.name})))

    const currentUser = useSelector((state) => state.user.currentUser)

    const [isLoading, setIsLoading] = useState(true)
    const [catalogs, setCatalogs] = useState([])
    const [formData, setFormData] = useState({})
    const [childrenValidation, setChildrenValidation] = useState({})
    const [isChildrenUpdateValid, setIsChildrenUpdateValid] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [message, setMessage] = useState(null)
    const [isManagedItem, setIsManagedItem] = useState(null)
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)

    useEffect(() => {
        if (solution.apps.some(x => x.appType === AppType.modelDriven)) {
            setIsManagedItem(true)
            defaultFormData.isManagedItem = true
        }
        setFormData(defaultFormData)
        async function fetchData() {
            await getCatalogs()
            setIsLoading(false)
        }
        if (showDialog && isLoading) {
            fetchData()
        }
    }, [showDialog])

    useEffect(() => {
        if (Object.keys(childrenValidation).length)
            setIsChildrenUpdateValid(Object.keys(childrenValidation).reduce((value, key) => {
                return value && childrenValidation[key];
            }, true))
    }, [childrenValidation])
    
    const onUpdateChildrenValidation = (key, isValid) => {
        setChildrenValidation(valid => ({ ...valid, [key]: isValid }))
    }

    const getCatalogs = async () => {
        const result = await Api.get(`/PowerPlatformAppStoreCatalog`)
        const data = result.data.map(x => ({key: x.id.toString(), text: x.name}))

        setCatalogs(data)

        if (!defaultFormData.catalogId)
            onFieldUpdate('catalogId', data[0].key)
    }

    const onFieldUpdate = (fieldName, value) => {
        setFormData((prevState) => ({
            ...prevState,
            [fieldName]: value
        }))
    }

    const onArdoqUpdate = (value) => {
        const ids = value.map(x => x.key)
        setFormData({
            ...formData,
            'ardoq': value,
            'ardoqIds': ids.join(',')
        })
    }

    const saveFormData = async () => {
        setIsSaving(true)
        setMessage(null)

        const form = new FormData()

        Object.keys(formData).forEach(key => {
            if (Array.isArray(formData[key])) {
                formData[key].forEach(file => {
                    form.append(key, file)
                })
            }
            else {
                form.append(key, formData[key])
            }
        })

        try {
            await Api.post("CreateAppStoreSubmissionItem", form, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            setMessage(
                <MessageBar
                    dismissButtonAriaLabel="Close"
                    truncated={true}
                    messageBarType={MessageBarType.success}>
                    Your request has been submitted.
                </MessageBar>
            )
            if (onSaveCallback)
                onSaveCallback()
        } catch (err) {
            console.error("Upload failed", err);
        }

        setIsSaving(false)
    }

    const publisherOptions = [{
            key: AppStoreIdentity.original, 
            text: `Use Original: ${solution.publisher}`
        }, {
            key: AppStoreIdentity.anonymous,
            text: 'Publish anonymously as Power Platform Team'
        }
    ]

    const authorOptions = [{
            key: AppStoreIdentity.original, 
            text: `Use Original: ${currentUser.displayName}`
        }, {
            key: AppStoreIdentity.anonymous,
            text: 'Publish anonymously as Power Platform Team'
        }
    ]

    const templateTooltip = 
        <div>
            <p>A Template is like a blueprint — people copy it and make their own version.</p>
            <p>Great for sharing ideas, patterns, demo apps, useful flows or solutions that need customization.</p>
            <p>The supported components for templates are:</p>
            <ul>
                <li>Table and its sub components like forms, saved queries, relationships, table maps.</li>
                <li>Environment variables.</li>
                <li>Canvas apps.</li>
                <li>Modern workflows.</li>
                <li>AiModel and its subcomponents like AiConfig.</li>
                <li>Web resources.</li>
                <li>Power Apps component framework components. This includes modern solution aware components created using the Power Apps component framework.</li>
            </ul>
        </div>

    const managedItemTooltip = 
        <div>
            <p>A Managed Item is a packaged, stable app or component that users can deploy without editing.</p>
            <p>Perfect for polished solutions meant for broader, consistent use.</p>
            <p><span role='img' aria-label='icon'>🔒</span> <i>A Managed Item is like installing an app — users use it, but don’t change the internals.</i></p>
        </div>

    const submissionTypesOptions = [{
            key: 'template',
            text: <div>Template <InfoTooltip content={templateTooltip} /></div>,
            disabled: !!isManagedItem
        }, {
            key: 'managedItem',
            text: <div>Managed Item <InfoTooltip content={managedItemTooltip} /></div>
        }
    ]

    const messagePanel = 
        <div style={{marginTop: 15}}>
            {message}
        </div>  

    const dialogFields = !isLoading &&
        <div style={dialogFormStyles}>
            <div style={twoColumnsContainer}>
                <div style={twoColumnsSingleColLabel}>
                    <label className='item-title'>Name</label>
                </div>
                <div style={twoColumnsSingleCol}>
                    <CustomTextField
                        defaultValue={solution.name}
                        disabled={true}
                    />
                </div>
            </div>
            <div>
                <div style={{marginBottom: 5}}>
                    <label className='item-title'>Description</label>
                </div>
                <div>
                    <CustomTextField
                        defaultValue={solution.description}
                        disabled={true}
                        multiline={true}
                    />
                </div>
            </div>
            <Separator />
            <div style={twoColumnsContainer}>
                <div style={twoColumnsSingleColLabel}>
                    <label className='item-title'>Primary component</label>
                </div>
                <div style={twoColumnsSingleCol}>
                    <CustomDropDown
                        defaultValue={formData.primaryComponentId ? formData.primaryComponentId : '-1'}
                        options={solutionComponents}
                        disabled={false}
                        onUpdate={v => onFieldUpdate('primaryComponentId', v)}
                        required={true}
                        onValidation={v => onUpdateChildrenValidation('primaryComponentId', v)}
                    />
                </div>
            </div>
            <div style={twoColumnsContainer}>
                <div style={twoColumnsSingleColLabel}>
                    <label className='item-title'>Business purpose <InfoTooltip content='Explain why this app is being published. This information will be reviewed by the BA Service Lead for approval before it appears in the catalog.' /></label>
                </div>
                <div style={twoColumnsSingleCol}>
                    <CustomTextField
                        defaultValue={formData.businessJustification}
                        disabled={false}
                        onUpdate={v => onFieldUpdate('businessJustification', v)}
                        required={true}
                        onValidation={v => onUpdateChildrenValidation('businessJustification', v)}
                        multiline={true}
                    />
                </div>
            </div>
            <div style={twoColumnsContainer}>
                <div style={twoColumnsSingleColLabel}>
                    <label className='item-title'>Business categories</label>
                </div>
                <div style={twoColumnsSingleCol}>
                    <DimensionPicker
                        itemSelector={onArdoqUpdate}
                        searchDataUrl='/Ardoq?depth=1'
                        isDisabled={false}
                        selection={formData.ardoq ? formData.ardoq : []}
                        isRequired={true}
                        treeDataUrl='/Ardoq?depth=1'
                        keyHidden={true}
                        isMultiValue={true}
                        onValidation={v => onUpdateChildrenValidation('ardoq', v)}
                    />
                </div>
            </div>
            <div style={twoColumnsContainerTop}>
                <div style={twoColumnsSingleColLabelTop}>
                    <label className='item-title'>Publisher</label>
                </div>
                <div style={twoColumnsSingleCol}>
                    <ChoiceGroup
                        options={publisherOptions}
                        defaultSelectedKey={formData.publisher}
                        disabled={false}
                        onChange={(e, v) => onFieldUpdate('publisher', v.key)}
                    />
                </div>
            </div>
            <div style={twoColumnsContainerTop}>
                <div style={twoColumnsSingleColLabelTop}>
                    <label className='item-title'>Author</label>
                </div>
                <div style={twoColumnsSingleCol}>
                    <ChoiceGroup
                        options={authorOptions}
                        defaultSelectedKey={formData.author}
                        disabled={false}
                        onUpdate={v => onFieldUpdate('author', v)}
                    />
                </div>
            </div>
            <div style={twoColumnsContainer}>
                <div style={twoColumnsSingleColLabel}>
                    <label className='item-title'>Publish to catalog</label>
                </div>
                <div style={twoColumnsSingleCol}>
                    <CustomDropDown
                        options={catalogs}
                        defaultValue={formData.catalogId}
                        disabled={false}
                        onUpdate={v => onFieldUpdate('catalogId', v)}
                        required={true}
                        onValidation={v => onUpdateChildrenValidation('catalogId', v)}
                    />
                </div>
            </div>
            <div style={twoColumnsContainerTop}>
                <div style={twoColumnsSingleColLabelTop}>
                    <label className='item-title'>Submission type</label>
                    <div style={{fontSize: 12, margin: '10px 0'}}>
                        <div style={{marginBottom: 5}}>Select how you want others in the organization to use your solution.</div>
                        <div>Choose <b>Template</b> if you're sharing an idea that others can build on.</div>
                        <div>Choose <b>Managed Item</b> if you're publishing something ready to use.</div>
                    </div>
                </div>
                <div style={twoColumnsSingleCol}>
                    <CustomRadioButton
                        options={submissionTypesOptions}
                        defaultValue={formData.isManagedItem === null ? null : (formData.isManagedItem ? 'managedItem' : 'template')}
                        onUpdate={v => onFieldUpdate('isManagedItem', v === 'managedItem')}
                        required={true}
                        onValidation={v => onUpdateChildrenValidation('isManagedItem', v)}
                    />
                    {
                        isManagedItem && <div style={{fontSize: 12, marginTop: 10}}>This solution includes a Model‑Driven App and must therefore be submitted as a Managed item.</div>
                    }
                </div>
            </div>
            <div style={{...twoColumnsContainer, alignItems: 'start'}}>
                <div style={twoColumnsSingleColLabel}>
                    <label className='item-title'>Icon</label>
                </div>
                <div style={twoColumnsSingleCol}>
                    <UploadFile 
                        defaultValue={defaultFormData.icon}
                        onUpdate={v => onFieldUpdate('icon', v)}
                        accept="image/*"
                        required={true}
                        onValidation={v => onUpdateChildrenValidation('icon', v)}
                    />
                </div>
            </div>
            <div style={{...twoColumnsContainer, alignItems: 'start'}}>
                <div style={twoColumnsSingleColLabel}>
                    <label className='item-title'>Screenshots</label>
                </div>
                <div style={twoColumnsSingleCol}>
                    <UploadFile 
                        defaultValue={defaultFormData.screenshots}
                        onUpdate={v => onFieldUpdate('screenshots', v)}
                        accept="image/*" 
                        multiple={true} 
                        required={true}
                        onValidation={v => onUpdateChildrenValidation('screenshots', v)}
                    />
                </div>
            </div>
            <div style={{...twoColumnsContainer, alignItems: 'start'}}>
                <div style={twoColumnsSingleColLabel}>
                    <label className='item-title'>Documentation <InfoTooltip content='If your solution connects to external data sources such as Excel, SharePoint, or others, the documentation must include the expected data structure and required data types for each source. This ensures proper integration, minimizes errors, and allows others who receive the app to replicate the solution successfully.' /></label>
                </div>
                <div style={twoColumnsSingleCol}>
                    <UploadFile 
                        defaultValue={defaultFormData.documentation}
                        onUpdate={v => onFieldUpdate('documentation', v)}
                        multiple={true} 
                        required={false}
                        onValidation={v => onUpdateChildrenValidation('documentation', v)}
                    />
                </div>
            </div>
        </div>

    const _onConfirmDialog = () => {
        saveFormData()
        setShowConfirmDialog(false)
    }

    const confirmationDialog = 
        <Dialog 
            hidden={!showConfirmDialog}
            maxWidth={700}
            dialogContentProps={{
                title: `Do you want to continue publishing?`,
            }}
            modalProps={{
                isBlocking: false
            }}
        >
            <div>
                <p><span role='img' aria-label='warning'>⚠️</span> <b>Please verify that your solution does not contain hardcoded configuration values.</b></p>
                <p>
                    This includes SharePoint URLs, website URLs, email addresses, environment IDs, list IDs, tenant-specific settings, and similar values.<br />
                    Where possible, configuration should be stored in <b>Environment Variables</b> so the solution can be deployed and used across different environments.
                </p>
                <p><b>Do you want to continue publishing?</b></p>
            </div>
            <DialogFooter>
                <PrimaryButton onClick={_onConfirmDialog} text="Proceed with Publishing" />
                <DefaultButton onClick={() => setShowConfirmDialog(false)} text="Cancel" />
            </DialogFooter>
        </Dialog>

    const dialogFooter = 
        <DialogFooter>
            { 
                isSaving
                ? <Spinner style={{ margin: '5px 20px' }} size={SpinnerSize.medium} />
                : !message && 
                    <div>
                        <PrimaryButton onClick={() => setShowConfirmDialog(true)} disabled={!isChildrenUpdateValid} text="Submit publishing request" styles={{root: { marginRight: 10}}} />
                        <DefaultButton onClick={() => setShowDialog(false)} text="Cancel" />
                    </div>
            }
            <div style={{margin: '-20px -4px'}}>
                <ActionButton iconProps={{iconName: 'info'}} href='https://abb.sharepoint.com/sites/powerplatformhub/SitePages/App-Catalog-publishing.aspx'>App Catalog publishing overview</ActionButton>
            </div>
        </DialogFooter>

    const form = 
        <div>
            {dialogFields}
            {dialogFooter}
            {messagePanel}
            {confirmationDialog}
        </div>

    const dialog = 
        <Dialog
            hidden={!showDialog}
            isBlocking={true}
            minWidth={800}
            maxWidth={800}
            dialogContentProps={{
                title: 'Power Platform Catalog Submission Form'
            }}
            onDismiss={e => setShowDialog(false)}
            styles={dialogStyles}
        >
            {                
                isLoading
                ? <LoadingIcon />
                : <Pivot styles={tabsStyles}>
                    <PivotItem itemKey='Request' headerText='Request'>
                        {form}
                    </PivotItem>
                    <PivotItem itemKey='History' headerText='History'>
                        <AppStoreSubmissionHistory submissionItems={submissionItems} />
                    </PivotItem>
                </Pivot>
            }
        </Dialog>
    
    
    return (
        <div>
            {dialog}
        </div>
    );
}

const dialogFormStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: 10
}

const twoColumnsContainer = {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    gap: 20,
    alignItems: 'center',
}

const twoColumnsContainerTop = {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    gap: 20,
    alignItems: 'top',
}

const twoColumnsSingleCol = {
    flex: 2,
    overflow: 'hidden'
}

const twoColumnsSingleColLabel = {
    flex: 1,
    overflow: 'hidden',
    maxWidth: 270
}

const twoColumnsSingleColLabelTop = {
    flex: 1,
    overflow: 'hidden',
    paddingTop: 10,
    maxWidth: 270
}

const tabsStyles = {
    itemContainer: {
        marginTop: 10
    }
}

const dialogStyles = {
    main: {
        maxWidth: '100% !important'
    }
}

export default AppStoreSubmissionForm


// WEBPACK FOOTER //
// ./src/components/EditSite/PowerAppEditElements/AppStoreSubmissionForm.js